import React, { FC } from 'react';
import { Box, Flex, Icon, Link, Text, globe } from '@amzn/storm-ui';
import styled from 'styled-components';
import ReadOnlyFormField from 'src/shared/components/form/ReadOnlyFormRow';
import useIntl from '@i18n/useIntl';
import {
  COMBINE_BILLING_INVOICE_RECIPIENT_LABEL_KEY,
  COMBINE_BILLING_ADDRESS_LABEL_KEY,
  COMBINE_BILLING_ADDRESS_SELLER_CENTRAL_KEY,
  COMBINE_BILLING_ADDRESS_SELLER_CENTRAL_LINK_KEY,
  COMBINE_BILLING_RECIPIENT_ADVERTISER_KEY,
  COMBINE_BILLING_RECIPIENT_MANAGER_KEY,
  COMBINE_BILLING_PAYMENT_PROFILES_LABEL_KEY,
  COMBINE_BILLING_PAYMENT_COUNTRIES_TAG_KEY,
  COMBINE_BILLING_PAYMENT_TYPE_LABEL_KEYS,
} from '../constants';
import type { BillingTabData } from '../types';

interface BillingSummaryTabProps {
  data: BillingTabData;
  tabId: string;
}

const CountryTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 8px;
  background: #b9d3f8;
  border-radius: 8px;
  font-size: 12px;
  line-height: 18px;
  color: #161d26;
`;

const ProfileLabel = styled.div`
  font-size: 14px;
  line-height: 20px;
  color: #161d26;
`;

/*
  Typography is inherited rather than stated: the link sits mid-sentence, and the design gives it
  the same family, weight, size, line height and letter spacing as the sentence around it. Storm's
  Link does not inherit those on its own, which is the same reason AmazonAdsAgreementLink wraps its
  anchor this way. A span and an anchor are both ordinary inline text, so neither introduces an
  atomic box the line breaker could break after, which is what would otherwise strand punctuation.
*/
const InlineLink = styled.span`
  && {
    font: inherit;
    letter-spacing: inherit;
  }

  && a {
    font: inherit;
    letter-spacing: inherit;
  }
`;

/**
 * Click-through to Seller Central, for use as the `{link}` argument of the seller billing address
 * sentence. Kept local because this tab is its only caller; extract it to a shared component if a
 * second surface ever renders the same string id. Exported only so a test can render it on its own:
 * the test bundle returns string ids rather than values, so the sentence never interpolates and the
 * link is unreachable through the tab.
 */
export const SellerAccountLink: FC<{ url: string }> = ({ url }) => {
  const { intl } = useIntl();

  return (
    <InlineLink>
      <Link href={url} target="_blank" external={true}>
        {intl.getMessage(COMBINE_BILLING_ADDRESS_SELLER_CENTRAL_LINK_KEY)}
      </Link>
    </InlineLink>
  );
};

/** One grouped payment profile row: display label + the countries it covers. */
interface PaymentProfileRow {
  paymentMethodType: string;
  countryCodes: string[];
}

/**
 * Groups the per-country payment agreement entries by payment method type so
 * each type renders once with its country coverage (per Figma: label + tag).
 * Types whose entries all lack a country code are dropped so no row renders
 * an empty ("0 Countries") coverage tag.
 */
const groupPaymentMethods = (
  paymentMethods: NonNullable<BillingTabData['paymentMethods']>,
): PaymentProfileRow[] => {
  const byType = new Map<string, string[]>();
  paymentMethods.forEach(({ paymentMethodType, countryCode }) => {
    if (!paymentMethodType) {
      return;
    }
    const countries = byType.get(paymentMethodType) ?? [];
    if (countryCode && !countries.includes(countryCode)) {
      countries.push(countryCode);
    }
    byType.set(paymentMethodType, countries);
  });
  return Array.from(byType.entries())
    .filter(([, countryCodes]) => countryCodes.length > 0)
    .map(([paymentMethodType, countryCodes]) => ({
      paymentMethodType,
      countryCodes,
    }));
};

const BillingSummaryTab: FC<BillingSummaryTabProps> = ({ data, tabId }) => {
  const { intl, locale } = useIntl();
  const { sellerCentralUrl } = data;

  const recipientLabel =
    data.invoiceRecipient === 'ADVERTISER_ACCOUNT'
      ? intl.getMessage(COMBINE_BILLING_RECIPIENT_ADVERTISER_KEY)
      : intl.getMessage(COMBINE_BILLING_RECIPIENT_MANAGER_KEY);

  // Only surface the billing address when there is exactly one bill-to address.
  // If the account has no bill-to profiles, or multiple distinct ones, hide the
  // address row rather than show an ambiguous/empty value. Unused for a seller,
  // whose address is never shown here at all (see the render below).
  const singleBillingAddress =
    data.billingAddresses && data.billingAddresses.length === 1
      ? data.billingAddresses[0]
      : undefined;

  const paymentProfileRows = data.paymentMethods
    ? groupPaymentMethods(data.paymentMethods)
    : [];

  const paymentTypeLabel = (paymentMethodType: string): string => {
    const labelKey = COMBINE_BILLING_PAYMENT_TYPE_LABEL_KEYS[paymentMethodType];
    return labelKey ? intl.getMessage(labelKey) : paymentMethodType;
  };

  /**
   * Localized display name for a country code (e.g. "us" → "United States"),
   * falling back to the uppercased code when the name cannot be resolved.
   */
  const countryDisplayName = (countryCode: string): string => {
    const code = countryCode.toUpperCase();
    try {
      return (
        new Intl.DisplayNames([locale], { type: 'region' }).of(code) ?? code
      );
    } catch {
      return code;
    }
  };

  const countryCoverageTag = (countryCodes: string[]): string =>
    countryCodes.length === 1
      ? countryDisplayName(countryCodes[0])
      : intl.formatMessage(COMBINE_BILLING_PAYMENT_COUNTRIES_TAG_KEY, {
          count: countryCodes.length,
        });

  return (
    <Box paddingBlockStart="xsmall">
      <ReadOnlyFormField
        id={`${tabId}-invoice-recipient`}
        label={intl.getMessage(COMBINE_BILLING_INVOICE_RECIPIENT_LABEL_KEY)}
        value={recipientLabel}
      />
      {/*
        A seller maintains billing details in Seller Central, so the row keeps its label but points
        there instead of showing an address, whatever billingAddresses holds. Every other account
        falls through to the single-address rule above.
      */}
      {sellerCentralUrl ? (
        <ReadOnlyFormField
          id={`${tabId}-billing-address`}
          label={intl.getMessage(COMBINE_BILLING_ADDRESS_LABEL_KEY)}
        >
          <Text
            data-testid={`${tabId}-billing-address-value`}
            color="secondary"
          >
            {intl.formatMessage(COMBINE_BILLING_ADDRESS_SELLER_CENTRAL_KEY, {
              link: <SellerAccountLink url={sellerCentralUrl} />,
            })}
          </Text>
        </ReadOnlyFormField>
      ) : (
        singleBillingAddress && (
          <ReadOnlyFormField
            id={`${tabId}-billing-address`}
            label={intl.getMessage(COMBINE_BILLING_ADDRESS_LABEL_KEY)}
            value={singleBillingAddress}
          />
        )
      )}
      {paymentProfileRows.length > 0 && (
        <ReadOnlyFormField
          id={`${tabId}-payment-profiles`}
          label={intl.getMessage(COMBINE_BILLING_PAYMENT_PROFILES_LABEL_KEY)}
        >
          <Flex direction="column" rowGap="small">
            {paymentProfileRows.map((row) => (
              <Flex
                key={row.paymentMethodType}
                direction="column"
                rowGap="xxxsmall"
                alignItems="flex-start"
              >
                <ProfileLabel>
                  {paymentTypeLabel(row.paymentMethodType)}
                </ProfileLabel>
                <CountryTag>
                  <Icon icon={globe} size="sm" />
                  {countryCoverageTag(row.countryCodes)}
                </CountryTag>
              </Flex>
            ))}
          </Flex>
        </ReadOnlyFormField>
      )}
    </Box>
  );
};

export default BillingSummaryTab;
